import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Badge,
  TextField,
  InputAdornment,
  CircularProgress,
  Tabs,
  Tab,
  Divider,
  Chip,
} from '@mui/material';
import { Search, People, Person, Add } from '@mui/icons-material';
import { useQuery } from 'react-query';
import { searchCandidates } from '../../../api/services/candidate';

const getInitials = (name) => {
  if (!name) return '?';
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

const getTimeLabel = (timestamp) => {
  if (!timestamp) return '';
  const time = new Date(timestamp);
  const now = new Date();
  const diff = now - time;

  if (diff < 3600000) {
    const mins = Math.floor(diff / 60000);
    return mins < 1 ? 'now' : `${mins}m`;
  }
  if (diff < 86400000) {
    return time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  if (diff < 172800000) return 'Yesterday';
  return time.toLocaleDateString([], { day: 'numeric', month: 'short' });
};

const CandidateRow = ({ candidate, onPress }) => (
  <ListItem
    button
    onClick={() => onPress(candidate)}
    sx={{
      px: 2,
      py: 1,
      borderRadius: 1.5,
      mx: 1,
      mb: 0.5,
      cursor: 'pointer',
      '&:hover': { bgcolor: '#F8FAFC' },
      '& *': {
        cursor: 'pointer',
      },
    }}
  >
    <ListItemAvatar sx={{ minWidth: 44 }}>
      <Avatar
        sx={{
          width: 36,
          height: 36,
          bgcolor: '#1570EF',
          fontSize: 13,
          fontWeight: 600,
        }}
      >
        {getInitials(`${candidate.first_name} ${candidate.last_name}`)}
      </Avatar>
    </ListItemAvatar>
    <ListItemText
      primary={
        <Typography fontSize={13.5} fontWeight={600} color="#0F172A" noWrap>
          {candidate.first_name} {candidate.last_name}
        </Typography>
      }
      secondary={
        <Typography fontSize={11.5} color="#94A3B8" noWrap>
          {candidate.primary_email ||
            candidate.mobile_phone ||
            'Start conversation'}
        </Typography>
      }
    />
    <Add sx={{ color: '#1570EF', fontSize: 18 }} />
  </ListItem>
);

const ChatRow = ({ chat, isSelected, onPress }) => (
  <ListItem
    button
    selected={isSelected}
    onClick={onPress}
    sx={{
      px: 2,
      py: 1.25,
      borderBottom: '1px solid #F1F5F9',
      cursor: 'pointer',
      backgroundColor: isSelected
        ? '#EFF6FF'
        : chat.hasNewMessage
        ? '#F8FAFF'
        : 'transparent',
      borderLeft: chat.hasNewMessage
        ? '3px solid #1570EF'
        : '3px solid transparent',
      '&:hover': { backgroundColor: '#F8FAFC' },
      '&.Mui-selected': {
        backgroundColor: '#EFF6FF',
        '&:hover': { backgroundColor: '#E0EDFF' },
      },
      '& *': {
        cursor: 'pointer',
      },
    }}
  >
    <ListItemAvatar sx={{ minWidth: 48, position: 'relative' }}>
      <Badge
        badgeContent={chat.unread}
        invisible={chat.unread === 0}
        sx={{
          '& .MuiBadge-badge': {
            fontSize: 10,
            minWidth: 18,
            height: 18,
            bgcolor: '#1570EF',
            color: '#FFFFFF',
            fontWeight: 600,
            border: '2px solid #FFFFFF',
          },
        }}
      >
        <Avatar
          sx={{
            width: 42,
            height: 42,
            bgcolor: chat.avatarBg,
            fontSize: 15,
            fontWeight: 600,
          }}
        >
          {getInitials(chat.name)}
        </Avatar>
      </Badge>
      {chat.hasNewMessage && (
        <Box
          sx={{
            position: 'absolute',
            top: -2,
            right: -2,
            width: 10,
            height: 10,
            borderRadius: '50%',
            bgcolor: '#1570EF',
            border: '2px solid #FFFFFF',
          }}
        />
      )}
    </ListItemAvatar>
    <ListItemText
      sx={{ m: 0 }}
      primary={
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Typography
            fontSize={13.5}
            fontWeight={chat.unread > 0 || chat.hasNewMessage ? 700 : 600}
            noWrap
            color="#0F172A"
            sx={{ flex: 1 }}
          >
            {chat.isDefault ? '⭐ ' : ''}
            {chat.name}
          </Typography>
          <Typography
            fontSize={11}
            color={chat.unread > 0 || chat.hasNewMessage ? '#1570EF' : '#94A3B8'}
            fontWeight={chat.unread > 0 || chat.hasNewMessage ? 600 : 500}
            flexShrink={0}
          >
            {chat.time}
          </Typography>
        </Box>
      }
      secondary={
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            mt: 0.25,
          }}
        >
          {chat.hasNewMessage && (
            <Box
              sx={{
                bgcolor: '#1570EF',
                px: 0.6,
                py: 0.1,
                borderRadius: 0.5,
                flexShrink: 0,
              }}
            >
              <Typography
                sx={{
                  fontSize: 9,
                  color: '#FFFFFF',
                  fontWeight: 700,
                  letterSpacing: 0.3,
                }}
              >
                NEW
              </Typography>
            </Box>
          )}
          <Typography
            fontSize={12.5}
            color={
              chat.unread > 0 || chat.hasNewMessage ? '#334155' : '#94A3B8'
            }
            fontWeight={chat.unread > 0 || chat.hasNewMessage ? 500 : 400}
            noWrap
            sx={{ flex: 1 }}
          >
            {chat.isGroup && chat.lastMsgSender
              ? `${chat.lastMsgSender}: `
              : ''}
            {chat.lastMessage}
          </Typography>
        </Box>
      }
    />
  </ListItem>
);

const MessageSidebar = ({
  selectedId,
  userId,
  onSelect,
  onLoadMore,
  hasMore,
  loadingMore,
  conversations,
  onStartNewConversation,
  newMessageIds,
  setNewMessageIds,
}) => {
  const [search, setSearch] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [candidates, setCandidates] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  const { refetch: searchCandidatesApi } = useQuery(
    ['searchCandidates', search],
    () => searchCandidates(search),
    {
      enabled: false,
      onSuccess: (data) => {
        setCandidates(data.results || data || []);
        setIsSearching(false);
      },
      onError: () => {
        setIsSearching(false);
        setCandidates([]);
      },
    }
  );

  useEffect(() => {
    if (search.trim()) {
      setIsSearching(true);
      const timer = setTimeout(() => {
        searchCandidatesApi();
      }, 500);
      return () => clearTimeout(timer);
    } else {
      setCandidates([]);
      setIsSearching(false);
    }
  }, [search, searchCandidatesApi]);

  const chatList = useMemo(() => {
    const source = conversations?.pages?.flatMap((p) => p.results || []) || [];
    return source
      .map((conv) => {
        const others =
          conv.participants?.filter((p) => p.id !== userId) || [];
        let name = 'Conversation';
        let isGroup = false;
        let isDefault = false;
        let avatarBg = '#1570EF';

        if (conv.is_group && conv.group) {
          isGroup = true;
          isDefault = conv.group.is_default || false;
          name = conv.group.name || 'Group Chat';
          avatarBg = '#7C3AED';
        } else if (others.length === 1) {
          name =
            `${others[0].first_name} ${others[0].last_name}`.trim() ||
            others[0].username ||
            'Unknown';
          avatarBg = '#1570EF';
        } else if (others.length > 1) {
          name = others.map((p) => p.first_name).join(', ');
          avatarBg = '#7C3AED';
        }

        const last = conv.last_message;
        const timeStr = last?.timestamp ? getTimeLabel(last.timestamp) : '';
        const hasNewMessage = newMessageIds?.has(conv.id) || false;

        return {
          id: conv.id,
          name,
          lastMessage: last?.content || 'No messages',
          time: timeStr,
          unread: conv.unread_count || 0,
          isDefault,
          isGroup,
          avatarBg,
          hasNewMessage,
          lastMsgSender:
            last?.sender?.first_name || last?.sender?.username || '',
        };
      })
      .sort((a, b) => {
        if (a.isDefault && !b.isDefault) return -1;
        if (!a.isDefault && b.isDefault) return 1;
        if (a.hasNewMessage && !b.hasNewMessage) return -1;
        if (!a.hasNewMessage && b.hasNewMessage) return 1;
        if (a.unread > 0 && b.unread === 0) return -1;
        if (a.unread === 0 && b.unread > 0) return 1;
        return 0;
      });
  }, [conversations, userId, newMessageIds]);

  const filteredChats = useMemo(() => {
    return tabValue === 0
      ? chatList.filter((c) => c.isGroup)
      : chatList.filter((c) => !c.isGroup);
  }, [chatList, tabValue]);

  const handleStartChat = (candidate) => {
    const userData = {
      user_id: candidate.user_id || candidate.id,
      first_name: candidate.first_name,
      last_name: candidate.last_name,
    };
    onStartNewConversation?.(userData);
    setSearch('');
    setCandidates([]);
  };

  const handleChatPress = (conv) => {
    if (newMessageIds?.has(conv.id)) {
      setNewMessageIds?.((prev) => {
        const newSet = new Set(prev);
        newSet.delete(conv.id);
        return newSet;
      });
    }
    onSelect(conv);
  };

  const renderContent = () => {
    if (isSearching) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 6,
          }}
        >
          <CircularProgress size={26} sx={{ color: '#1570EF' }} />
          <Typography sx={{ color: '#94A3B8', fontSize: 13, mt: 1.5 }}>
            Searching...
          </Typography>
        </Box>
      );
    }

    if (
      search.trim() &&
      candidates.length === 0 &&
      filteredChats.length === 0
    ) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <Typography sx={{ color: '#94A3B8', fontSize: 13 }}>
            No results found
          </Typography>
        </Box>
      );
    }

    if (filteredChats.length === 0 && !search.trim()) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 6,
            px: 3,
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              bgcolor: '#F1F5F9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 1.5,
            }}
          >
            {tabValue === 0 ? (
              <People sx={{ color: '#94A3B8', fontSize: 26 }} />
            ) : (
              <Person sx={{ color: '#94A3B8', fontSize: 26 }} />
            )}
          </Box>
          <Typography
            sx={{ color: '#0F172A', fontSize: 14, fontWeight: 600, mb: 0.5 }}
          >
            {tabValue === 0 ? 'No groups yet' : 'No conversations yet'}
          </Typography>
          <Typography
            sx={{
              color: '#94A3B8',
              fontSize: 12.5,
              textAlign: 'center',
              lineHeight: 1.5,
            }}
          >
            {tabValue === 0
              ? 'Group chats will appear here'
              : 'Search for people to start a chat'}
          </Typography>
        </Box>
      );
    }

    return (
      <>
        {search.trim() && candidates.length > 0 && (
          <Box sx={{ pt: 1.5, pb: 0.5 }}>
            <Typography
              sx={{
                fontSize: 10.5,
                fontWeight: 700,
                color: '#94A3B8',
                letterSpacing: 0.6,
                px: 2,
                py: 0.75,
              }}
            >
              PEOPLE · {candidates.length}
            </Typography>
            {candidates.map((candidate) => (
              <CandidateRow
                key={candidate.id}
                candidate={candidate}
                onPress={handleStartChat}
              />
            ))}
            {filteredChats.length > 0 && (
              <Divider sx={{ my: 1.5, borderColor: '#F1F5F9' }} />
            )}
          </Box>
        )}

        {filteredChats.length > 0 && (
          <List sx={{ p: 0 }}>
            {filteredChats.map((chat) => {
              const fullConv = conversations?.pages
                ?.flatMap((p) => p.results || [])
                .find((c) => c.id === chat.id);
              return (
                <ChatRow
                  key={chat.id}
                  chat={chat}
                  isSelected={selectedId === chat.id}
                  onPress={() => fullConv && handleChatPress(fullConv)}
                />
              );
            })}
          </List>
        )}

        {!search.trim() && hasMore && (
          <Box sx={{ py: 2, textAlign: 'center' }}>
            {loadingMore ? (
              <CircularProgress size={22} sx={{ color: '#1570EF' }} />
            ) : (
              <Typography
                onClick={onLoadMore}
                sx={{
                  color: '#1570EF',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: 600,
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                Load more
              </Typography>
            )}
          </Box>
        )}
      </>
    );
  };

  return (
    <Box
      sx={{
        width: { xs: '100%', md: 360 },
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#FFFFFF',
        borderRight: { md: '1px solid #E2E8F0' },
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box sx={{ px: 2.5, pt: 2.5, pb: 1.5 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Typography
            sx={{
              fontSize: 22,
              fontWeight: 700,
              color: '#0F172A',
              letterSpacing: -0.3,
            }}
          >
            Messages
          </Typography>
          <Chip
            label={`${chatList.length}`}
            size="small"
            sx={{
              height: 22,
              fontSize: 11,
              fontWeight: 600,
              bgcolor: '#EFF6FF',
              color: '#1570EF',
              borderRadius: 1.5,
            }}
          />
        </Box>
        <TextField
          fullWidth
          size="small"
          placeholder="Search people..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              bgcolor: '#F1F5F9',
              fontSize: 13.5,
              height: 40,
              '& fieldset': { border: 'none' },
              '&:hover fieldset': { border: 'none' },
              '&.Mui-focused fieldset': { border: '1.5px solid #1570EF' },
              '&.Mui-focused': { bgcolor: '#FFFFFF' },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: '#64748B', fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Tabs */}
      <Tabs
        value={tabValue}
        onChange={(e, v) => {
          setTabValue(v);
          setSearch('');
        }}
        variant="fullWidth"
        sx={{
          minHeight: 42,
          borderBottom: '1px solid #F1F5F9',
          '& .MuiTab-root': {
            minHeight: 42,
            textTransform: 'none',
            fontSize: 13,
            fontWeight: 600,
            color: '#64748B',
            cursor: 'pointer',
            '&.Mui-selected': { color: '#1570EF' },
          },
          '& .MuiTabs-indicator': { bgcolor: '#1570EF', height: 2.5 },
        }}
      >
        <Tab
          icon={<People sx={{ fontSize: 18 }} />}
          iconPosition="start"
          label={`Groups (${chatList.filter((c) => c.isGroup).length})`}
        />
        <Tab
          icon={<Person sx={{ fontSize: 18 }} />}
          iconPosition="start"
          label={`People (${chatList.filter((c) => !c.isGroup).length})`}
        />
      </Tabs>

      {/* List */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: '#CBD5E1',
            borderRadius: 3,
            '&:hover': { bgcolor: '#94A3B8' },
          },
        }}
      >
        {renderContent()}
      </Box>
    </Box>
  );
};

export default MessageSidebar;